import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Card from '../common/Card';
import Button from '../common/Button';
import AttendanceService from '../../services/attendance.service';
import CalendarService from '../../services/calendar.service';
import LeaveService from '../../services/leave.service';
import WFHService from '../../services/wfh.service';
import WorkScheduleService from '../../services/workSchedule.service';
import { ScheduleOverrideDialog, localDateKey } from './WorkScheduleManager';
import { useAuth } from '../../contexts/AuthContext';
import toast from '../../utils/toast';
import { isSameDay, parseHolidayDate } from '../../utils/holidayUtils';
import { formatDateDisplay } from '../../utils/dateDisplay';
import './calendar-holidays.css';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EVENT_COLORS = {
  holiday: 'bg-rose-100 text-rose-700 border border-rose-200',
  holidayPending: 'bg-amber-100 text-amber-700 border border-amber-200',
  wfh: 'bg-amber-100 text-amber-700 border border-amber-200',
  leave: 'bg-violet-100 text-violet-700 border border-violet-200',
  google: 'bg-sky-100 text-sky-700 border border-sky-200',
  present: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  halfday: 'bg-orange-100 text-orange-700 border border-orange-200'
};

const getResponseArray = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.events)) return data.events;
  if (Array.isArray(data?.holidays)) return data.holidays;
  if (Array.isArray(data?.leaveRequests)) return data.leaveRequests;
  if (Array.isArray(data?.requests)) return data.requests;
  if (Array.isArray(data?.attendanceData)) return data.attendanceData;
  return [];
};

const normalizeStatus = (status) => String(status || '').toUpperCase();

const getCacheKey = (year, month, country) => `${year}-${month}-${String(country || 'IN').toUpperCase()}`;

const toDayOnly = (value) => {
  const date = parseHolidayDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const dateToIso = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isoToDdMmYyyy = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = String(isoDate).split('-');
  if (!year || !month || !day) return '';
  return `${day}/${month}/${year}`;
};

const normalizeAttendanceStatus = (value) => {
  const status = String(value || '').toLowerCase().replace(/\s+/g, '');
  if (status === 'present') return 'present';
  if (status === 'halfday' || status === 'half-day') return 'halfday';
  return null;
};

const getCalendarPillLabel = (entry) => {
  if (!entry) return '';
  if (entry.kind === 'google') return entry.title || 'Event';
  if (entry.kind === 'halfday') return 'Half day';
  if (entry.kind === 'wfh') return 'WFH';
  return entry.kind;
};

const formatHolidayDate = (value) => {
  const date = toDayOnly(value);
  if (!date) return '';
  return formatDateDisplay(date);
};

const resolveHolidayDbId = (h) => {
  if (h?.databaseId != null) return String(h.databaseId);
  if (h?.id != null && /^\d+$/.test(String(h.id))) return String(h.id);
  const rawId = h?.id;
  if (rawId != null && String(rawId).startsWith('db-')) return String(rawId).slice(3);
  return null;
};

/** Union month API + /holidays without dropping same-day pending vs approved rows. */
const mergeHolidayListsForCalendar = (monthly, internalExtra) => {
  const keyFor = (h) => {
    const pk = resolveHolidayDbId(h);
    if (pk) return `id:${pk}`;
    const d = toDayOnly(h.date);
    const dk = d ? dateToIso(d) : String(h.date || '');
    const approved =
      h.isCompanyApproved === true || h.is_company_approved === true || String(h.status || '').toUpperCase() === 'APPROVED';
    const name = String(h.name || h.title || '').trim().toLowerCase();
    return `row:${dk}|${name}|${approved ? '1' : '0'}`;
  };
  const map = new Map();
  for (const h of monthly) {
    map.set(keyFor(h), h);
  }
  for (const h of internalExtra) {
    const k = keyFor(h);
    if (!map.has(k)) map.set(k, h);
  }
  return [...map.values()].sort((a, b) => {
    const dateA = parseHolidayDate(a.date);
    const dateB = parseHolidayDate(b.date);
    if (!dateA || !dateB) return 0;
    return dateA.getTime() - dateB.getTime();
  });
};

const isHolidayPendingApproval = (item) => {
  if (!item || (item.kind !== 'holiday' && item.kind !== 'holidayPending')) return false;
  if (item.kind === 'holidayPending') return true;
  const approved = item.isCompanyApproved === true || item.is_company_approved === true || item.status === 'APPROVED';
  if (approved) return false;
  return true;
};

const isHolidayKind = (item) => item?.kind === 'holiday' || item?.kind === 'holidayPending';

const listDatesInRange = (fromDate, toDate) => {
  const start = toDayOnly(fromDate);
  const end = toDayOnly(toDate);
  if (!start || !end || start > end) return [];
  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
/** Exclusive end for Google Calendar API timeMax: first instant of the month after `date`. */
const startOfNextMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

const formatGoogleTime = (event) => {
  if (event.allDay) return 'All day';
  if (!event.startDateTime) return 'Multi-day';
  const start = new Date(event.startDateTime);
  if (Number.isNaN(start.getTime())) return 'Scheduled';
  return start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** Google all-day events use an exclusive end.date; timed events use inclusive end. */
const expandGoogleEventToDays = (event) => {
  const startD = toDayOnly(event.start);
  if (!startD) return [];
  let endD = toDayOnly(event.end || event.start);
  if (event.allDay && endD) {
    const endInclusive = new Date(endD);
    endInclusive.setDate(endInclusive.getDate() - 1);
    endD = endInclusive;
  }
  if (!endD || endD < startD) endD = startD;
  const startDayKey = dateToIso(startD);
  const days = listDatesInRange(startD, endD);
  return days.map((day) => {
    const dk = dateToIso(day);
    return {
      id: `google-${event.id}-${dk}`,
      date: dk,
      title: event.title || 'Event',
      kind: 'google',
      source: 'google',
      startDateTime: dk === startDayKey ? event.start : null,
      endDateTime: event.end,
      allDay: Boolean(event.allDay),
      location: event.location || '',
      htmlLink: event.htmlLink || ''
    };
  });
};

const getGoogleReturnTo = () => `${window.location.origin}/calendar`;

const ModernCalendar = ({ selectedCountry = 'IN', isHolidayManager = false }) => {
  const { isAdmin, isHR } = useAuth();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [scheduleDays, setScheduleDays] = useState({});
  const [scheduleRevision, setScheduleRevision] = useState(0);
  const [scheduleError, setScheduleError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [pendingWFH, setPendingWFH] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleStatusLoading, setGoogleStatusLoading] = useState(true);

  const holidayCacheRef = useRef(new Map());
  /** In-flight month fetches: share promise so parallel loads never return an empty list. */
  const holidayInFlightRef = useRef(new Map());
  const googleTokenInvalidToastRef = useRef(false);
  const googleSyncErrorToastRef = useRef(false);
  const googleDbSetupToastRef = useRef(false);
  const holidayPendingNoticeRef = useRef(new Set());

  const monthLabel = `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  const scheduleMonthStart = localDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const scheduleMonthEnd = localDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
  useEffect(() => {
    let active = true;
    WorkScheduleService.calendar(scheduleMonthStart, scheduleMonthEnd).then(days => {
      if (active) { setScheduleDays(Object.fromEntries(days.map(day => [day.date, day]))); setScheduleError(''); }
    }).catch(error => { if (active) { setScheduleDays({}); setScheduleError(error.response?.data?.message || 'Unable to load work schedules'); } });
    return () => { active = false; };
  }, [scheduleMonthStart, scheduleMonthEnd, scheduleRevision]);

  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const leading = Array.from({ length: firstDay.getDay() }, () => null);
    const days = Array.from({ length: lastDay.getDate() }, (_, index) => new Date(year, month, index + 1));
    return [...leading, ...days];
  }, [currentDate]);

  const monthlyEvents = useMemo(() => {
    const grouped = new Map();
    events.forEach((event) => {
      const eventDate = toDayOnly(event.date);
      if (!eventDate) return;
      const key = dateToIso(eventDate);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(event);
    });
    return grouped;
  }, [events]);

  const selectedEvents = useMemo(() => {
    const key = dateToIso(selectedDate);
    return monthlyEvents.get(key) || [];
  }, [monthlyEvents, selectedDate]);

  const fetchMonthlyHolidays = async (year, month, countryCode) => {
    const cacheKey = getCacheKey(year, month, countryCode);
    const cache = holidayCacheRef.current;
    const inFlight = holidayInFlightRef.current;

    if (cache.has(cacheKey)) return cache.get(cacheKey);
    if (inFlight.has(cacheKey)) return inFlight.get(cacheKey);

    const promise = (async () => {
      try {
        const payload = await CalendarService.getMonthlyHolidays(year, month, countryCode);
        const list = Array.isArray(payload.holidays)
          ? payload.holidays.map((holiday) => ({
              ...holiday,
              source: holiday.source || 'database'
            }))
          : [];
        const result = {
          holidays: list,
          meta: {
            pendingCount: payload.pendingCount,
            source: payload.source,
            message: payload.message
          }
        };
        cache.set(cacheKey, result);
        return result;
      } finally {
        inFlight.delete(cacheKey);
      }
    })();

    inFlight.set(cacheKey, promise);
    return promise;
  };

  const prefetchUpcomingMonth = async () => {
    const futureMonth = new Date(currentDate);
    futureMonth.setMonth(futureMonth.getMonth() + 1);
    await fetchMonthlyHolidays(
      futureMonth.getFullYear(),
      futureMonth.getMonth() + 1,
      selectedCountry
    ).catch(() => {});
  };

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const rangeStart = startOfMonth(currentDate);
      const rangeEndExclusive = startOfNextMonth(currentDate);
      const monthlyHolidaysPromise = fetchMonthlyHolidays(year, month, selectedCountry);

      const [
        monthlyHolidayBundle,
        internalHolidaysRes,
        wfhCalendarRes,
        leaveCalendarRes,
        googleEventsRes,
        attendanceRes,
        pendingWFHRes,
        pendingLeavesRes
      ] = await Promise.all([
        monthlyHolidaysPromise,
        CalendarService.getInternalHolidays().catch(() => []),
        WFHService.getWFHCalendar(year, month).catch(() => ({ events: [] })),
        LeaveService.getLeaveCalendar(year, month).catch(() => ({ events: [] })),
        CalendarService.getGoogleEvents(rangeStart.toISOString(), rangeEndExclusive.toISOString()).catch((err) => {
          const d = err.response?.data;
          return {
            connected: d?.connected ?? false,
            events: Array.isArray(d?.events) ? d.events : [],
            syncError: d?.syncError || d?.error || err.message || 'Google Calendar request failed',
            needsReconnect: d?.needsReconnect,
            tokenInvalid: d?.tokenInvalid,
            databaseSetupRequired: d?.databaseSetupRequired
          };
        }),
        AttendanceService.getAttendance(
          isoToDdMmYyyy(dateToIso(rangeStart)),
          isoToDdMmYyyy(dateToIso(new Date(year, currentDate.getMonth() + 1, 0)))
        ).catch(() => ({ attendanceData: [] })),
        WFHService.getMyRequests().catch(() => []),
        LeaveService.getMyRequests().catch(() => [])
      ]);

      const monthlyHolidayData = monthlyHolidayBundle?.holidays || [];
      const holidayMeta = monthlyHolidayBundle?.meta || {};
      if (
        isHolidayManager &&
        (holidayMeta.pendingCount > 0 || holidayMeta.source === 'pending_hr_review' || holidayMeta.source === 'api_imported_pending')
      ) {
        const nk = `${year}-${month}`;
        if (!holidayPendingNoticeRef.current.has(nk)) {
          holidayPendingNoticeRef.current.add(nk);
          toast.info(
            holidayMeta.message ||
              `Suggested public holidays are waiting for approval (${holidayMeta.pendingCount || 0}). Open the Holiday management tab above.`
          );
        }
      }

      const googlePayload = googleEventsRes || {};
      const isGoogleConnected = Boolean(googlePayload.connected);
      setGoogleConnected(isGoogleConnected);
      setGoogleStatusLoading(false);
      if (googlePayload.tokenInvalid && !googleTokenInvalidToastRef.current) {
        googleTokenInvalidToastRef.current = true;
        toast.error('Google Calendar link is invalid. Disconnect and connect again.');
      }
      if (googlePayload.syncError && !googleSyncErrorToastRef.current) {
        googleSyncErrorToastRef.current = true;
        const hint = googlePayload.needsReconnect
          ? ' Disconnect Google Calendar under the calendar page and connect again.'
          : '';
        toast.error(`Google Calendar: ${googlePayload.syncError}${hint}`);
      }
      if (googlePayload.databaseSetupRequired && !googleDbSetupToastRef.current) {
        googleDbSetupToastRef.current = true;
        toast.error('Google Calendar is not configured on the server. Ask an admin to run database sync for HRMS.');
      }

      const internalHolidays = getResponseArray(internalHolidaysRes)
        .filter((holiday) => {
          const date = toDayOnly(holiday.date);
          return date && date.getMonth() === currentDate.getMonth() && date.getFullYear() === year;
        })
        .map((holiday) => ({
          ...holiday,
          type: 'holiday',
          source: 'internal'
        }));

      const mergedHolidayRows = mergeHolidayListsForCalendar(monthlyHolidayData, internalHolidays);
      const holidayEvents = mergedHolidayRows.map((holiday) => ({
        ...(() => {
          const approved =
            holiday.isCompanyApproved ??
            holiday.is_company_approved ??
            (String(holiday.status || '').toUpperCase() === 'APPROVED');
          return {
            kind: approved ? 'holiday' : 'holidayPending',
            isCompanyApproved: Boolean(approved)
          };
        })(),
        id: `holiday-${holiday.id || holiday.name}-${holiday.date}`,
        date: holiday.date,
        title: holiday.name || holiday.title || 'Holiday',
        source: holiday.source || 'database',
        description: holiday.description || holiday.details || holiday.localName || ''
      }));

      const wfhEvents = getResponseArray(wfhCalendarRes)
        .filter((event) => normalizeStatus(event.status) === 'APPROVED')
        .map((event) => ({
          id: `wfh-${event.id}-${event.start}`,
          date: event.start || event.date,
          title: event.title || 'WFH',
          kind: 'wfh',
          status: event.status,
          employee: event.employee,
          reason: event.reason
        }));

      const leaveEvents = getResponseArray(leaveCalendarRes)
        .filter((event) => normalizeStatus(event.status) === 'APPROVED')
        .flatMap((event) => {
          const days = listDatesInRange(event.start || event.fromDate, event.end || event.toDate);
          return days.map((day) => ({
            id: `leave-${event.id}-${dateToIso(day)}`,
            date: dateToIso(day),
            title: event.title || 'Leave',
            kind: 'leave',
            status: event.status,
            employee: event.employee,
            reason: event.reason
          }));
        });

      const rawGoogle = Array.isArray(googlePayload.events) ? googlePayload.events : [];
      const googleEvents = rawGoogle.flatMap(expandGoogleEventToDays);
      const attendanceEvents = getResponseArray(attendanceRes)
        .map((entry) => {
          const status = normalizeAttendanceStatus(entry.status);
          if (!status) return null;
          const date = toDayOnly(entry.datestring || entry.date);
          if (!date) return null;
          return {
            id: `attendance-${entry.id || entry.pk || entry.datestring || dateToIso(date)}-${status}`,
            date: dateToIso(date),
            title: status === 'present' ? 'Present' : 'Half day',
            kind: status
          };
        })
        .filter(Boolean);

      setEvents([...holidayEvents, ...wfhEvents, ...leaveEvents, ...attendanceEvents, ...googleEvents]);

      const pendingWfhItems = getResponseArray(pendingWFHRes)
        .filter((request) => normalizeStatus(request.status) === 'PENDING')
        .slice(0, 5);
      setPendingWFH(pendingWfhItems);

      const pendingLeaveItems = getResponseArray(pendingLeavesRes)
        .filter((request) => normalizeStatus(request.status) === 'PENDING')
        .slice(0, 5);
      setPendingLeaves(pendingLeaveItems);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const res = await CalendarService.getGoogleConnectUrl(getGoogleReturnTo());
      const url = res?.url;
      if (!url) {
        toast.error('Google connect URL is unavailable');
        return;
      }
      window.location.assign(url);
    } catch {
      toast.error('Failed to connect Google Calendar');
    }
  };

  const promoteGoogleToCompanyHoliday = async (item) => {
    if (!item?.title || !item?.date) return;
    try {
      await CalendarService.promoteGoogleEvent({
        name: item.title,
        date: item.date,
        type: 'COMPANY',
        description: `Promoted from Google Calendar: ${item.title}`
      });
      toast.success('Added as a company holiday');
      holidayCacheRef.current.clear();
      loadCalendar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add company holiday');
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await CalendarService.disconnectGoogleCalendar();
      setGoogleConnected(false);
      toast.success('Google Calendar disconnected');
      loadCalendar();
    } catch {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('googleCalendarStatus');
    if (oauthStatus === 'connected') toast.success('Google Calendar connected');
    if (oauthStatus === 'error') toast.error('Google Calendar connection failed');
    if (oauthStatus === 'db_setup_required') {
      toast.error('Database not ready for Google tokens. Run: cd libs/db && npm run safe-sync');
    }
    if (oauthStatus) {
      const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
    prefetchUpcomingMonth();
  }, [currentDate, selectedCountry]);

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const changeMonth = (offset) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + offset);
      return next;
    });
  };

  const selectDay = (day) => setSelectedDate(day);

  return (
    <div className="space-y-6">
      {scheduleError && <p role="alert" className="rounded bg-amber-50 p-3 text-sm text-amber-900">{scheduleError}</p>}
      {(isAdmin || isHR) && <div className="flex justify-end"><Button onClick={() => setOverrideOpen(true)}>Override Day</Button></div>}
      {(isAdmin || isHR) && <ScheduleOverrideDialog open={overrideOpen} date={localDateKey(selectedDate)} onClose={() => setOverrideOpen(false)} onSaved={() => { setScheduleRevision(value => value + 1); loadCalendar(); }} />}
      <Card>
        <Card.Body className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-semibold text-gray-900">{monthLabel}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => changeMonth(-1)} icon={ChevronLeftIcon} />
              <Button size="sm" variant="outline" onClick={goToToday}>Today</Button>
              <Button size="sm" variant="outline" onClick={() => changeMonth(1)} icon={ChevronRightIcon} />
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <Card>
            <Card.Body className="p-4 md:p-5">
              <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                {WEEK_DAYS.map((day) => <span key={day}>{day}</span>)}
              </div>

              <div className="mt-2 grid grid-cols-7 gap-2">
                {monthDays.map((day, index) => {
                  if (!day) return <div key={`empty-${index}`} className="calendar-day-empty" />;
                  const dayKey = dateToIso(day);
                  const dayEvents = monthlyEvents.get(dayKey) || [];
                  const holiday = dayEvents.find((entry) => isHolidayKind(entry));
                  const isPendingHoliday = holiday && isHolidayPendingApproval(holiday);
                  const isToday = isSameDay(day, new Date());
                  const isSelected = isSameDay(day, selectedDate);

                  return (
                    <button
                      type="button"
                      key={dayKey}
                      className={[
                        'calendar-day-cell',
                        isToday ? 'calendar-day-today' : '',
                        isSelected ? 'calendar-day-selected' : '',
                        holiday ? (isPendingHoliday ? 'calendar-day-holiday-pending' : 'calendar-day-holiday') : ''
                      ].join(' ')}
                      onClick={() => selectDay(day)}
                    >
                      <div className="flex items-start justify-between">
                        <span className="text-sm font-semibold text-gray-900">{day.getDate()}</span>
                        {holiday && (
                          <CalendarDaysIcon className={`h-3.5 w-3.5 ${isPendingHoliday ? 'text-amber-500' : 'text-rose-500'}`} />
                        )}
                      </div>
                      {holiday && (
                        <p className={`mt-1 truncate text-[11px] font-medium ${isPendingHoliday ? 'text-amber-700' : 'text-rose-700'}`}>{holiday.title}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {scheduleDays[localDateKey(day)]?.managed && scheduleDays[localDateKey(day)].status !== 'OFFICE' && <span className="calendar-pill bg-blue-50 text-blue-800" title={scheduleDays[localDateKey(day)].source}>
                          {scheduleDays[localDateKey(day)].status === 'WO' ? 'Holiday' : scheduleDays[localDateKey(day)].status === 'WFH' ? 'WFH Working' : scheduleDays[localDateKey(day)].status}
                          {scheduleDays[localDateKey(day)].overrideId ? ' · HR Override' : ''}
                        </span>}
                        {dayEvents
                          .filter((entry) => !isHolidayKind(entry))
                          .slice(0, 3)
                          .map((entry) => (
                            <span
                              key={entry.id}
                              title={getCalendarPillLabel(entry)}
                              className={`calendar-pill ${EVENT_COLORS[entry.kind] || EVENT_COLORS.google}`}
                            >
                              {getCalendarPillLabel(entry)}
                            </span>
                          ))}
                        {dayEvents.length > 4 && <span className="text-[10px] text-gray-500">+{dayEvents.length - 4}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="space-y-6 xl:col-span-4">
          <Card className="calendar-side-panel">
            <Card.Header className="calendar-side-panel-header flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-gray-900">
                {formatDateDisplay(selectedDate)}
              </h3>
              {googleConnected ? (
                <Button size="sm" variant="outline" onClick={disconnectGoogleCalendar}>Disconnect Google</Button>
              ) : (
                <Button size="sm" onClick={connectGoogleCalendar}>Connect Google</Button>
              )}
            </Card.Header>

            <Card.Body className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">This day</p>
              {scheduleDays[localDateKey(selectedDate)]?.managed && scheduleDays[localDateKey(selectedDate)].status !== 'OFFICE' && <div className="rounded-lg bg-blue-50 p-3 text-sm">
                <p className="font-medium">{scheduleDays[localDateKey(selectedDate)].status === 'WO' ? 'Holiday' : scheduleDays[localDateKey(selectedDate)].status === 'WFH' ? 'Working — WFH' : scheduleDays[localDateKey(selectedDate)].status}</p>
                <p>{scheduleDays[localDateKey(selectedDate)].source}</p><p>{scheduleDays[localDateKey(selectedDate)].reason}</p>
                {(isAdmin || isHR) && scheduleDays[localDateKey(selectedDate)].overrideId && <Button size="sm" variant="outline" onClick={() => setOverrideOpen(true)}>View / edit override</Button>}
              </div>}
              {!googleConnected && !googleStatusLoading && (
                <p className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                  Personal Google events appear here after you use <strong>Connect Google</strong> and sign in with the account you want on the calendar.
                </p>
              )}
              {selectedEvents.length === 0 ? (
                <p className="text-sm text-gray-600">Nothing scheduled for this day.</p>
              ) : (
                <ul className="space-y-2">
                  {selectedEvents.map((item) => (
                    <li
                      key={item.id}
                      className={`calendar-day-item-row rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2 ${EVENT_COLORS[item.kind] || ''}`}
                    >
                      <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
                      {isHolidayKind(item) ? (
                        <>
                          <p className="text-xs text-gray-700">{formatHolidayDate(item.date)}</p>
                          {isHolidayPendingApproval(item) ? (
                            <p className="text-xs font-semibold text-amber-800">
                              Not a company holiday (pending approval)
                            </p>
                          ) : (
                            <p className="text-xs font-semibold text-emerald-800">Approved holiday</p>
                          )}
                        </>
                      ) : (
                        <p className="text-[11px] capitalize text-gray-600">
                          {item.kind === 'google' ? formatGoogleTime(item) : item.kind}
                        </p>
                      )}
                      {isHolidayKind(item) && item.description ? (
                        <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-gray-700">{item.description}</p>
                      ) : null}
                      {item.kind === 'google' && item.location ? (
                        <p className="truncate text-[11px] text-gray-500">{item.location}</p>
                      ) : null}
                      {item.kind === 'google' && item.htmlLink ? (
                        <a className="text-[11px] text-primary-600 hover:underline" href={item.htmlLink} target="_blank" rel="noreferrer">
                          Open in Google Calendar
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h3 className="font-semibold text-gray-900">Pending</h3>
            </Card.Header>
            <Card.Body className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-800">WFH Requests</p>
                  <Link className="text-xs text-primary-600 hover:underline" to="/wfh-request">View all</Link>
                </div>
                {pendingWFH.length === 0 ? (
                  <p className="text-xs text-gray-500">No pending WFH requests.</p>
                ) : (
                  pendingWFH.map((request) => (
                    <div key={request.id} className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      {formatDateDisplay(request.date)} - {request.reason}
                    </div>
                  ))
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-800">Leave Requests</p>
                  <Link className="text-xs text-primary-600 hover:underline" to="/leave-request">View all</Link>
                </div>
                {pendingLeaves.length === 0 ? (
                  <p className="text-xs text-gray-500">No pending leave requests.</p>
                ) : (
                  pendingLeaves.map((request) => (
                    <div key={request.id} className="mb-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
                      {formatDateDisplay(request.fromDate || request.date)} - {request.reason || request.leaveType}
                    </div>
                  ))
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Card>
        <Card.Body className="grid grid-cols-2 gap-2 text-xs md:grid-cols-7">
          <span className={`calendar-pill ${EVENT_COLORS.holiday}`}>Holiday (approved)</span>
          <span className={`calendar-pill ${EVENT_COLORS.holidayPending}`}>Holiday (not company)</span>
          <span className={`calendar-pill ${EVENT_COLORS.wfh}`}>WFH</span>
          <span className={`calendar-pill ${EVENT_COLORS.leave}`}>Leave</span>
          <span className={`calendar-pill ${EVENT_COLORS.present}`}>Present</span>
          <span className={`calendar-pill ${EVENT_COLORS.halfday}`}>Half day</span>
          <span className={`calendar-pill ${EVENT_COLORS.google}`}>Google events</span>
        </Card.Body>
      </Card>

      {loading && (
        <p className="text-sm text-gray-500">Refreshing calendar data...</p>
      )}
    </div>
  );
};

export default ModernCalendar;
